import axios from 'axios';
import {get, post, put, downloadFile} from './Tools';

/**
 * Servicio específico para el resumen de nómina PDF
 * Centraliza todas las operaciones relacionadas con la generación de PDFs de resumen de nómina
 */

class ResumenNominaService {
    constructor() {
        this.baseURL = (process.env.REACT_APP_SERVER || '').replace(/\/+$/, '');
        this.apiBaseURL = /\/v1\/api$/i.test(this.baseURL)
            ? this.baseURL
            : `${this.baseURL}/v1/api`;
    }

    /**
     * Genera un PDF de resumen de nómina con los datos proporcionados
     * @param {Object} data - Datos del resumen de nómina
     * @param {Object} data.company_data - Información de la empresa
     * @param {Array} data.percepciones - Lista de percepciones
     * @param {Array} data.deducciones - Lista de deducciones
     * @param {Array} data.otros - Lista de otros conceptos
     * @returns {Promise<Blob>} - Archivo PDF como blob
     */
    async generarPDF(data) {
        try {
            // Usar axios directamente para mejor manejo de blobs
            const token = localStorage.getItem('token');
            const response = await axios.post(`${this.apiBaseURL}/pdf/resumen-nomina`, data, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'x-access-token': '1'
                },
                responseType: 'blob'
            });
            
            // Verificar que la respuesta sea válida
            if (!response || !response.data) {
                throw new Error('No se recibió respuesta del servidor');
            }

            // Verificar el tamaño del blob - si es muy pequeño, probablemente es un error
            if (response.data.size < 1000) { // Menos de 1KB probablemente es un error
                console.warn('El archivo PDF es muy pequeño, verificando si es un error...');
                
                // Intentar leer el contenido como texto para ver si es un error JSON
                const text = await response.data.text();
                try {
                    const errorData = JSON.parse(text);
                    throw new Error(`Error del servidor: ${errorData.message || errorData.error || 'Error desconocido'}`);
                } catch (parseError) {
                    // Si no es JSON, verificar si es HTML de error
                    if (text.includes('<html') || text.includes('<!DOCTYPE')) {
                        throw new Error('El servidor devolvió una página de error HTML en lugar de un PDF');
                    }
                    // Si es texto plano, mostrar el contenido
                    throw new Error(`Error del servidor: ${text}`);
                }
            }

            // Verificar el Content-Type
            const contentType = response.headers['content-type'];
            if (contentType && !contentType.includes('application/pdf')) {
                console.warn(`Content-Type inesperado: ${contentType}`);
            }

            console.log(`PDF generado correctamente. Tamaño: ${response.data.size} bytes`);
            return response.data;
        } catch (error) {
            console.error('Error al generar PDF de resumen de nómina:', error);
            throw error;
        }
    }

    /**
     * Genera un PDF de ejemplo de resumen de nómina
     * @returns {Promise<Blob>} - Archivo PDF de ejemplo como blob
     */
    async generarPDFEjemplo() {
        try {
            const response = await downloadFile(`${this.apiBaseURL}/pdf/resumen-nomina/ejemplo`, 'ejemplo_resumen_nomina.pdf');
            return response;
        } catch (error) {
            console.error('Error al generar PDF de ejemplo:', error);
            throw error;
        }
    }

    /**
     * Descarga un archivo PDF desde un blob
     * @param {Blob} blob - Archivo blob a descargar
     * @param {string} filename - Nombre del archivo
     */
    descargarPDF(blob, filename) {
        try {
            // Verificar que window y URL estén disponibles
            if (typeof window === 'undefined' || !window.URL) {
                throw new Error('window.URL no está disponible en este entorno');
            }

            // Verificar que el blob sea válido
            if (!blob || !(blob instanceof Blob)) {
                throw new Error('El archivo blob no es válido');
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            
            // Limpiar después de un breve delay
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error('Error al descargar PDF:', error);
            throw error;
        }
    }

    /**
     * Obtiene preview de conceptos agrupados para permitir personalización
     * @param {number} num_periodo - Número de período
     * @param {number} anio - Año
     * @param {number} tipo_periodo - ID del tipo de período
     * @returns {Promise<Object>} - Conceptos agrupados con preview
     */
    async obtenerPreview(num_periodo, anio, tipo_periodo) {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${this.apiBaseURL}/pdf/resumen-nomina/preview`,
                null,
                {
                    params: {
                        num_periodo,
                        anio,
                        tipo_periodo
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-access-token': '1'
                    }
                }
            );
            
            return response.data;
        } catch (error) {
            console.error('Error al obtener preview:', error);
            throw error;
        }
    }

    /**
     * Genera PDF con configuración personalizada
     * @param {Object} config - Configuración personalizada
     * @param {number} config.num_periodo - Número de período
     * @param {number} config.anio - Año
     * @param {number} config.tipo_periodo - ID del tipo de período
     * @param {Array} config.percepciones - Lista de percepciones seleccionadas
     * @param {Array} config.deducciones - Lista de deducciones seleccionadas
     * @param {Array} config.otros - Lista de otros conceptos
     * @returns {Promise<void>}
     */
    async generarPDFPersonalizado(config) {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${this.apiBaseURL}/pdf/resumen-nomina/generar`,
                config,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                        'x-access-token': '1'
                    },
                    responseType: 'blob'
                }
            );

            if (!response || !response.data) {
                throw new Error('No se recibió respuesta del servidor');
            }

            this.descargarPDF(response.data, 'resumen_nomina_personalizado.pdf');
        } catch (error) {
            console.error('Error al generar PDF personalizado:', error);
            throw error;
        }
    }

    /**
     * Genera y descarga un PDF de resumen de nómina
     * @param {Object} data - Datos del resumen de nómina
     * @param {string} filename - Nombre del archivo (opcional)
     * @returns {Promise<void>}
     */
    async generarYDescargarPDF(num_periodo, anio, tipo_periodo, filename = null) {
        try {
            const response = await downloadFile(`${this.apiBaseURL}/pdf/resumen-nomina/${num_periodo}/${anio}/${tipo_periodo}`, 'resumen_nomina.pdf');
            return response;
        } catch (error) {
            console.error('Error al generar y descargar PDF:', error);
        }
    }

    /**
     * Genera y descarga un PDF de ejemplo
     * @returns {Promise<void>}
     */
    async generarYDescargarPDFEjemplo() {
        try {
            const response = await downloadFile(`${this.apiBaseURL}/pdf/resumen-nomina/ejemplo`, 'ejemplo_resumen_nomina.pdf');
            return response;
            /*const blob = await this.generarPDFEjemplo();
            const filename = `ejemplo_resumen_nomina_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
            this.descargarPDF(blob, filename);*/
        } catch (error) {
            console.error('Error al generar y descargar PDF de ejemplo:', error);
            throw error;
        }
    }


    /**
     * Crea datos de ejemplo para testing
     * @returns {Object} - Datos de ejemplo
     */
    crearDatosEjemplo() {
        return {
            company_data: {
                name: 'Empresa Ejemplo S.A. de C.V.',
                rfc: 'EJE123456789',
                periodo_num: '1',
                year: '2024',
                fecha_inicio: '01/Ene/2024',
                fecha_fin: '15/Ene/2024',
                estatus: 'TIMBRADO',
                tipo_periodo: 'QUINCENAL'
            },
            percepciones: [
                { codigo: '001', concepto: 'SUELDO', descripcion: 'Sueldo base', monto: 35000.75, exento: 0.00 },
                { codigo: '002', concepto: 'BONO', descripcion: 'Bono de productividad', monto: 5000.00, exento: 0.00 }
            ],
            deducciones: [
                { codigo: '051', concepto: 'ISR', descripcion: 'Impuesto sobre la renta', monto: 8500.00, exento: 0.00 },
                { codigo: '052', concepto: 'IMSS', descripcion: 'Seguro social', monto: 1200.00, exento: 0.00 }
            ],
            otros: [
                { codigo: '090', concepto: 'CUOTA PATRONAL', descripcion: 'Cuota patronal IMSS', monto: 7000.00, exento: 0.00, num_empleados: 50 }
            ]
        };
    }
}

// Crear una instancia única del servicio
const resumenNominaService = new ResumenNominaService();

// Exportar la instancia por defecto
export default resumenNominaService;

// También exportar la clase para casos especiales
export { ResumenNominaService };
